import { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import {
    AppBar,
    Box,
    CssBaseline,
    Toolbar,
    useMediaQuery,
    useTheme,
    styled,
} from '@mui/material';

import SideBar from './sideBar';
import Header from 'components/header';
import { BRAND } from 'themes';
import { DRAWER_WIDTH, HEADER_HEIGHT, MINI_DRAWER_WIDTH } from 'utils/constants';

const Main = styled('main', {
    shouldForwardProp: (prop) => prop !== 'open' && prop !== 'isMobile',
})<{ open: boolean; isMobile: boolean }>(({ theme, open, isMobile }: any) => {
    const drawerWidth = isMobile ? 0 : open ? DRAWER_WIDTH : MINI_DRAWER_WIDTH;

    return {
        ...theme.typography.mainContent,
        marginLeft: 0,
        marginRight: 0,
        marginTop: `${HEADER_HEIGHT}px`,
        minHeight: `calc(100vh - ${HEADER_HEIGHT}px)`,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        borderRadius: 0,
        padding: '24px',
        width: `calc(100% - ${drawerWidth}px)`,
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    };
});

const MainLayout = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [sidebarOpen, setSidebarOpen] = useState(true);

    useEffect(() => {
        setSidebarOpen(!isMobile);
    }, [isMobile]);

    const drawerWidth = isMobile ? 0 : sidebarOpen ? DRAWER_WIDTH : MINI_DRAWER_WIDTH;

    const toggleSidebar = () => setSidebarOpen((prev) => !prev);

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: theme.palette.background.default }}>
            <CssBaseline />
            <AppBar
                enableColorOnDark
                position='fixed'
                color='inherit'
                elevation={0}
                sx={{
                    width: `calc(100% - ${drawerWidth}px)`,
                    ml: `${drawerWidth}px`,
                    height: HEADER_HEIGHT,
                    justifyContent: 'center',
                    bgcolor: '#fff',
                    color: BRAND.ink,
                    borderBottom: `1px solid ${BRAND.border}`,
                    boxShadow: '0 8px 24px rgba(27, 19, 64, 0.08)',
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        bottom: 0,
                        height: 2,
                        background: `linear-gradient(90deg, ${BRAND.primary} 0%, ${BRAND.secondary} 46%, rgba(85, 70, 255, 0) 100%)`,
                        pointerEvents: 'none',
                    },
                    transition: theme.transitions.create(['width', 'margin'], {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.enteringScreen,
                    }),
                }}>
                <Toolbar sx={{ minHeight: `${HEADER_HEIGHT}px !important`, px: { xs: 1.5, md: 3 } }}>
                    <Header sidebarOpen={sidebarOpen} onToggleSidebar={toggleSidebar} />
                </Toolbar>
            </AppBar>
            <SideBar open={sidebarOpen} onToggle={toggleSidebar} />
            <Main theme={theme} open={sidebarOpen} isMobile={isMobile}>
                <Outlet />
            </Main>
        </Box>
    );
};

export default MainLayout;
